import {
  QueryDocumentSnapshot,
  collection,
  getDocs,
} from 'firebase/firestore'
import FirestoreUser from '../types/FirestoreUser'
import { ReportInfo } from '../types/ReportTypes'
import { checkAllStepsCompleted } from './reportCompletion'
import { primaryServices } from './firebaseServiceContext'

/**
 * Fetches report metadata for an array of user documents.
 * This utility centralizes the logic for fetching report information
 * used across the application.
 *
 * @param docs - Array of user document snapshots
 * @param existingReports - Previously fetched reports (for pagination/updates)
 * @param clear - Whether to clear existing reports
 * @returns Record mapping user IDs to their report information
 */
export async function fetchUserReportsData(
  docs: QueryDocumentSnapshot<FirestoreUser>[],
  existingReports: Record<string, ReportInfo> = {},
  clear: boolean = false
): Promise<Record<string, ReportInfo>> {
  // This helper is only used by admin pages that read reports across users.
  // It must retain the administrator's primary identity while Act as User is on.
  const db = primaryServices.firestore
  const reports: Record<string, ReportInfo> = clear
    ? {}
    : { ...existingReports }

  // Get current month in YYYY-MM format
  const now = new Date()
  const currentMonthReport = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}`

  await Promise.all(
    docs.map(async (doc) => {
      const reportsRef = collection(db, 'users', doc.id, 'reports')
      const reportsSnap = await getDocs(reportsRef)

      // Build a lookup map from the already-fetched snapshot data
      const reportDocsById = new Map(reportsSnap.docs.map((d) => [d.id, d]))
      const reportDocIds = reportsSnap.docs.map((d) => d.id)
      const hasCurrentMonth = reportDocsById.has(currentMonthReport)

      // Find the most recent report by sorting the report IDs (YYYY-MM format)
      let mostRecentReport = ''
      if (reportDocIds.length > 0) {
        const sortedReports = [...reportDocIds].sort().reverse()
        mostRecentReport = sortedReports[0]
      }

      // Read allStepsCompleted directly from the fetched snapshot.
      // Fall back to computing it live from report data so users who completed
      // all steps before the flag was introduced still show as green.
      const isComplete = (id: string): boolean => {
        const snap = reportDocsById.get(id)
        if (!snap) return false
        const data = snap.data() as any
        return !!data.allStepsCompleted || checkAllStepsCompleted(data)
      }

      const currentMonthAllStepsCompleted = hasCurrentMonth
        ? isComplete(currentMonthReport)
        : undefined

      const mostRecentReportCompleted = mostRecentReport
        ? isComplete(mostRecentReport)
        : undefined

      reports[doc.id] = {
        hasReports: !reportsSnap.empty,
        currentMonth: hasCurrentMonth,
        currentMonthAllStepsCompleted,
        reports: reportDocIds,
        mostRecentReport: mostRecentReport,
        mostRecentReportCompleted,
      }
    })
  )

  return reports
}
